var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { MGPFallible, Utils } from '@everyboard/lib';
import { GameNode } from '../../../jscaip/AI/GameNode';
import { Coord } from '../../../jscaip/Coord';
import { GameStatus } from '../../../jscaip/GameStatus';
import { Ordinal } from '../../../jscaip/Ordinal';
import { Player, PlayerOrNone } from '../../../jscaip/Player';
import { ConfigurableRules } from '../../../jscaip/Rules';
import { RulesFailure } from '../../../jscaip/RulesFailure';
import { TableUtils } from '../../../jscaip/TableUtils';
import { Debug } from '../../../utils/Debug';
import { ReversiFailure } from './ReversiFailure';
import { ReversiMove } from './ReversiMove';
import { ReversiState } from './ReversiState';
export class ReversiMoveWithSwitched {
    move;
    switched;
    constructor(move, switched) {
        this.move = move;
        this.switched = switched;
    }
}
export class ReversiNode extends GameNode {
}
class RectangularBoardMode {
    getNextCoord(coord, direction, _) {
        return coord.getNext(direction);
    }
}
class ToricBoardMode {
    getNextCoord(coord, direction, state) {
        return coord.getNextToric(direction, state.getWidth(), state.getHeight());
    }
}
let AbstractReversiRules = class AbstractReversiRules extends ConfigurableRules {
    toricBoardMode = new ToricBoardMode();
    rectangularBoardMode = new RectangularBoardMode();
    getBoardMode(config) {
        if (config.toric) {
            return this.toricBoardMode;
        }
        else {
            return this.rectangularBoardMode;
        }
    }
    getInitialState(config) {
        const board = TableUtils.create(config.width, config.height, PlayerOrNone.NONE);
        const downRightCenter = new Coord(Math.floor(config.width / 2), Math.floor(config.height / 2));
        board[downRightCenter.y - 1][downRightCenter.x - 1] = Player.ZERO;
        board[downRightCenter.y][downRightCenter.x] = Player.ZERO;
        board[downRightCenter.y - 1][downRightCenter.x] = Player.ONE;
        board[downRightCenter.y][downRightCenter.x - 1] = Player.ONE;
        return new ReversiState(board, 0);
    }
    applyLegalMove(move, state, _, info) {
        const turn = state.turn;
        const player = state.getCurrentPlayer();
        const board = state.getCopiedBoard();
        if (move.equals(ReversiMove.PASS)) { // if the player pass
            const sameBoardDifferentTurn = new ReversiState(board, turn + 1);
            return sameBoardDifferentTurn;
        }
        for (const s of info) {
            board[s.y][s.x] = player;
        }
        board[move.coord.y][move.coord.x] = player;
        const resultingState = new ReversiState(board, turn + 1);
        return resultingState;
    }
    getAllSwitchedCoords(move, player, state, config) {
        // try the move, do it if legal, and return the switched pieces
        const switcheds = [];
        const opponent = player.getOpponent();
        const boardMode = this.getBoardMode(config);
        for (const direction of Ordinal.ORDINALS) {
            const firstSpace = boardMode.getNextCoord(move.coord, direction, state);
            if (state.hasPieceAt(firstSpace, opponent)) {
                // let's test this direction
                const switchedInDir = this.getSandwicheds(player, direction, firstSpace, state, config);
                for (const switched of switchedInDir) {
                    switcheds.push(switched);
                }
            }
        }
        return switcheds;
    }
    getSandwicheds(capturer, direction, start, state, config) {
        /**
          * expected that 'start' is in range, and is captured
          * if we don't reach another capturer, returns []
          * else : return all the coord between start and the first 'capturer' found (exluded)
          */
        const boardMode = this.getBoardMode(config);
        const sandwichedsCoord = [start]; // here we know it in range and captured
        let testedCoord = boardMode.getNextCoord(start, direction, state);
        while (state.isOnBoard(testedCoord) && testedCoord.equals(start) === false) {
            const testedCoordContent = state.getPieceAt(testedCoord);
            if (testedCoordContent === capturer) {
                // we found a sandwicher, in range, in this direction
                return sandwichedsCoord;
            }
            else if (testedCoordContent.isNone()) {
                // we found the emptyness before a capturer, so there won't be a next space
                return [];
            }
            else {
                // we found a switched/captured
                sandwichedsCoord.push(testedCoord); // we add it
                testedCoord = boardMode.getNextCoord(testedCoord, direction, state);
            }
        }
        return []; // we found the end of the board before we found the new piece like 'searchedPawn'
    }
    isGameEnded(state, config) {
        return this.playerCanOnlyPass(state, config) &&
            this.nextPlayerCanOnlyPass(state, config);
    }
    getGameStatus(node, config) {
        const state = node.gameState;
        const gameIsEnded = this.isGameEnded(state, config);
        if (gameIsEnded === false) {
            return GameStatus.ONGOING;
        }
        const scores = state.countScore();
        const diff = scores.get(Player.ONE) - scores.get(Player.ZERO);
        if (diff < 0) {
            return GameStatus.ZERO_WON;
        }
        if (diff > 0) {
            return GameStatus.ONE_WON;
        }
        return GameStatus.DRAW;
    }
    playerCanOnlyPass(state, config) {
        const currentPlayerChoices = this.getListMoves(state, config);
        // if the current player cannot start, then the part is ended
        return (currentPlayerChoices.length === 1) &&
            currentPlayerChoices[0].move.equals(ReversiMove.PASS);
    }
    nextPlayerCanOnlyPass(reversiState, config) {
        const nextBoard = reversiState.getCopiedBoard();
        const nextTurn = reversiState.turn + 1;
        const nextState = new ReversiState(nextBoard, nextTurn);
        return this.playerCanOnlyPass(nextState, config);
    }
    getListMoves(state, config) {
        const moves = [];
        const player = state.getCurrentPlayer();
        const opponent = state.getCurrentOpponent();
        for (const coordAndContent of state.getCoordsAndContents()) {
            const coord = coordAndContent.coord;
            if (state.getPieceAt(coord).isNone()) {
                // For each empty spaces
                const opponentNeighbors = state.getNeighboringPawnLike(opponent, coord);
                if (opponentNeighbors.length > 0) {
                    // if one of the 8 neighboring space is an opponent then, there could be a switch,
                    // and hence a legal move
                    const move = new ReversiMove(coord.x, coord.y);
                    const result = this.getAllSwitchedCoords(move, player, state, config);
                    if (result.length > 0) {
                        // there was switched piece and hence, a legal move
                        for (const switched of result) {
                            Utils.assert(player !== state.getPieceAt(switched), switched + 'was already switched!');
                        }
                        moves.push(new ReversiMoveWithSwitched(move, result.length));
                    }
                }
            }
        }
        if (moves.length === 0) {
            // When the user cannot move, their only move is to pass, which they cannot do otherwise
            // The board remains unchanged, only the turn changed, and the move is a "pass"
            moves.push(new ReversiMoveWithSwitched(ReversiMove.PASS, 0));
        }
        return moves;
    }
    isLegal(move, state, config) {
        if (move.equals(ReversiMove.PASS)) { // if the player passes
            // let's check that pass is a legal move right now
            // if there was no choice but to pass, then passing is legal!
            // else, passing was illegal
            if (this.playerCanOnlyPass(state, config)) {
                return MGPFallible.success([]);
            }
            else {
                return MGPFallible.failure(RulesFailure.CANNOT_PASS());
            }
        }
        if (state.getPieceAt(move.coord).isPlayer()) {
            return MGPFallible.failure(RulesFailure.MUST_CLICK_ON_EMPTY_SPACE());
        }
        const switched = this.getAllSwitchedCoords(move, state.getCurrentPlayer(), state, config);
        if (switched.length === 0) {
            return MGPFallible.failure(ReversiFailure.NO_ELEMENT_SWITCHED());
        }
        else {
            return MGPFallible.success(switched);
        }
    }
};
AbstractReversiRules = __decorate([
    Debug.log
], AbstractReversiRules);
export { AbstractReversiRules };
